<template>
  <div class="page-wrap">

    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Updates"
      subtitle="Broadcast important messages to all members"
      add-label="Add"
      :show-edit="true"
      :show-delete="true"
      @add="openAddDialog"
      @edit="openEditDialog"
      @delete="confirmDeleteDialog($event.id)"
      :columns="columns"
      :items="announcementData"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >

      <!-- Custom Cells -->
      <template #col-title="{ item }">
        <v-menu
          open-on-hover
          bottom
          offset-y
          nudge-bottom="0"
          :close-delay="300"
          max-width="500"
          content-class="premium-menu-tooltip"
          :disabled="!isTruncated(item.title, 80)"
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="cell-clamp-1" v-bind="attrs" v-on="on">
              {{ item.title }}
            </div>
          </template>
          <div class="premium-tooltip-card">
            <div class="premium-tooltip-scroll" style="white-space: pre-line;">
              {{ item.title }}
            </div>
          </div>
        </v-menu>
      </template>

      <!-- <template #col-member="{ item }">
        <div class="member-cell">
          <v-icon size="14" class="mr-1" color="#94A3B8">mdi-account-outline</v-icon>
          <span>{{ item.contact_person_name || item.name || 'Admin' }}</span>
        </div>
      </template> -->

      <template #col-date="{ item }">
        <span class="date-text">{{ item.created_at | formatDateTime }}</span>
      </template>

    </PremiumTable>

    <!-- ── Add/Edit Modal ──────────────────── -->
    <PremiumModal
      v-model="addDialogBox"
      :title="(committee.id ? 'Edit' : 'New') + ' Update'"
      :loading="loading"
      :submit-text="committee.id ? 'Update' : 'Post'"
      max-width="460"
      @close="closeDialog"
      @submit="committee.id ? updateAnnouncement(committee.id) : saveAnnouncement()"
    >
      <PremiumInput
        v-model="committee.title"
        label="Message / Title"
        placeholder="Type your update message here..."
        multiline
        rows="4"
        resizable
        v-validate="'required'"
        data-vv-as="Message"
        :error="errors.first('title') || serverSideErrors.title"
        name="title"
      />
    </PremiumModal>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Update"
      message="Are you sure you want to delete this update? It will be removed from all users' dashboards immediately."
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteAnnouncement"
    />

  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal  from "../../common/ConfirmModal.vue";
import PremiumModal  from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import filterMixin   from "../../../mixins/filterMixin";

export default {
  name: 'AnnouncementList',
  mixins: [filterMixin],
  components: { PremiumTable, ConfirmModal, PremiumModal, PremiumInput },

  data() {
    return {
      search:           "",
      announcementData: [],
      totalRecords:     0,
      loading:          false,
      deleteLoading:    false,
      addDialogBox:     false,
      deleteDialog:     false,
      delete_id:        "",
      page:             1,
      itemsPerPage:     10,
      committee: {
        id:    null,
        title: "",
      },
      options: {
        sortBy:   ["created_at"],
        sortDesc: [true],
      },
      serverSideErrors: {},
      user: {},
    };
  },

  computed: {
    ...mapState('theme', ['isDark', 'primaryColor', 'buttonColor']),

    columns() {
      return [
        { key: 'title',  label: 'Message / Title', width: '400px' },
        // { key: 'member', label: 'Posted By', width: '180px' },
        { key: 'date',   label: 'Post Date',  width: '180px' },
      ];
    },
  },

  watch: {
    page() { this.getData(); },
    itemsPerPage() { this.getData(); },
    search() {
      this.page = 1;
      this.getData();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    /** Show tooltip only when text is long enough that it likely gets truncated */
    isTruncated(text, threshold = 80) {
      return text && text.length > threshold;
    },

    onSearch(val) {
      this.search = val;
    },

    onOptionsUpdate(opt) {
      this.options.sortBy   = opt.sortBy || ["created_at"];
      this.options.sortDesc = opt.sortDesc || [true];
      this.getData();
    },

    getData() {
      this.loading = true;
      const sortBy   = this.options.sortBy[0] || "";
      const sortDesc = this.options.sortDesc[0] || "";

      const params = [
        `page=${this.page}`,
        `perPage=${this.itemsPerPage}`,
        `sortBy=${sortBy}`,
        `sortDesc=${sortDesc}`,
        `search=${this.search || ""}`
      ].join('&');

      this.$axios.get("societyadmin/announcement?" + params).then(response => {
        this.loading = false;
        const data = response.data;
        if (data.status) {
          this.announcementData = data.data;
          this.totalRecords     = data.total;
        } else {
          this.showError(data.message);
          if (data.type === 'invalid') {
            localStorage.removeItem("user");
            this.$router.push("/societyadmin/login");
          }
        }
      }).catch(() => { this.loading = false; });
    },

    saveAnnouncement() {
      this.$validator.validateAll().then(success => {
        if (success) {
          this.$axios.post("/societyadmin/announcement", this.committee).then(response => {
            const data = response.data;
            if (data.status) {
              this.setAlert({ showAlert: true, message: data.message, variant: "green accent-4", timeout: 2000 });
              this.getData();
              this.closeDialog();
            } else {
              this.handleErrors(data.errors, data.message);
            }
          });
        }
      });
    },

    updateAnnouncement(id) {
      this.$validator.validateAll().then(success => {
        if (success) {
          this.$axios.put("/societyadmin/announcement/" + id, this.committee).then(response => {
            const data = response.data;
            if (data.status) {
              this.setAlert({ showAlert: true, message: data.message, variant: "green accent-4", timeout: 2000 });
              this.getData();
              this.closeDialog();
            } else {
              this.handleErrors(data.errors, data.message);
            }
          });
        }
      });
    },

    deleteAnnouncement() {
      this.deleteLoading = true;
      this.$axios.delete("societyadmin/announcement/" + this.delete_id).then(response => {
        this.deleteLoading = false;
        const data = response.data;
        if (data.status) {
          this.deleteDialog = false;
          this.getData();
          this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: data.message });
        } else {
          this.showError(data.message);
        }
      }).catch(() => { this.deleteLoading = false; });
    },

    openAddDialog() {
      this.committee = { id: null, title: "" };
      this.addDialogBox = true;
    },

    openEditDialog(item) {
      this.committee = { id: item.id, title: item.title };
      this.addDialogBox = true;
    },

    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    closeDialog() {
      this.addDialogBox = false;
      this.deleteDialog = false;
      this.committee = { id: null, title: "" };
      this.$validator.reset();
      this.serverSideErrors = {};
    },

    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "N/A";
    },

    showError(msg) {
      this.setAlert({ showAlert: true, variant: "red accent-3", timeout: "2000", message: msg });
    },

    handleErrors(errors, message) {
      if (errors) {
        this.serverSideErrors = {};
        errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
      } else {
        this.showError(message);
      }
    },

    debounceSearch: debounce(function() {
      this.page = 1;
      this.getData();
    }, 500)
  },

  mounted() {
    this.user   = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : {};
    
    // Initialize persistent filters
    this.initFilters('announcement');

    this.getData();
    if (this.$route.query.add === '1') {
      this.$nextTick(() => this.openAddDialog());
    }
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Cells ── */
.cell-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--tx);
  cursor: default;
}
.member-cell { display: flex; align-items: center; font-size: 13px; font-weight: 600; color: var(--tx2); }
.date-text { font-size: 12.5px; color: var(--tx3); font-weight: 500; }

.cm2-field { display: flex; flex-direction: column; gap: 8px; }
.cm2-label { font-size: 12px; font-weight: 700; color: var(--tx3, #64748B); text-transform: uppercase; letter-spacing: 0.04em; }

@keyframes cm2FadeIn {
  from { opacity: 0; transform: scale(0.96) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

.premium-tooltip-card {
  background: var(--brand, #30384d);
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
}

.premium-tooltip-scroll {
  max-height: 250px;
  overflow-y: auto;
  display: block;
  font-size: 13px;
  line-height: 1.6;
  padding-right: 4px;
}

/* Custom scrollbar for tooltips */
.premium-tooltip-scroll::-webkit-scrollbar {
  width: 5px;
}
.premium-tooltip-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}
.premium-tooltip-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 10px;
}
.premium-tooltip-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.35);
}
</style>
